/**
 * Exact Euclidean feature transform on a grid (Felzenszwalb & Huttenlocher, separable): for every cell, the index of
 * the nearest seed cell by center distance. Row pass is a two-sweep nearest-seed scan (the input is binary, and rows
 * are contiguous); column pass is the lower envelope of parabolas over the row distances. O(cells).
 */

/**
 * @param {Int32Array} seeds per cell (row-major), a seed where `seeds[c] >= 0`
 * @param {number} width
 * @param {number} height
 * @param {Int32Array} [out] result buffer, length ≥ width · height (reused across bakes)
 * @param {Int32Array} [rowCol] scratch, same length
 * @returns {Int32Array} nearest seed cell index per cell, −1 when the grid has no seed
 */
export function nearestSeed(seeds, width, height, out, rowCol) {
	const n = width * height
	out = out || new Int32Array(n)
	rowCol = rowCol || new Int32Array(n)
	// Pass 1, rows: column of the nearest seed in the same row, −1 for none.
	for (let y = 0; y < height; y++) {
		const base = y * width
		let last = -1
		for (let x = 0; x < width; x++) {
			if (seeds[base + x] >= 0) last = x
			rowCol[base + x] = last
		}
		last = -1
		for (let x = width - 1; x >= 0; x--) {
			const c = base + x
			if (seeds[c] >= 0) last = x
			if (last >= 0 && (rowCol[c] < 0 || last - x < x - rowCol[c])) rowCol[c] = last
		}
	}
	// Pass 2, columns: argmin over q of (y − q)² + f(q), f(q) = squared row distance to the row's nearest seed.
	const f = new Float64Array(height)
	const v = new Int32Array(height)
	const z = new Float64Array(height + 1)
	for (let x = 0; x < width; x++) {
		let k = -1
		for (let q = 0; q < height; q++) {
			const col = rowCol[q * width + x]
			if (col < 0) {
				f[q] = Infinity
				continue
			}
			// f holds f(q) + q², the parabola's constant term; intersections compare without dividing.
			const gq = (col - x) * (col - x) + q * q
			f[q] = gq
			let num = 0
			let den = 1
			while (k >= 0) {
				const p = v[k]
				num = gq - f[p]
				den = 2 * (q - p)
				if (num > z[k] * den) break
				k--
			}
			k++
			v[k] = q
			z[k] = k === 0 ? -Infinity : num / den
			z[k + 1] = Infinity
		}
		if (k < 0) {
			for (let q = 0; q < height; q++) out[q * width + x] = -1
			continue
		}
		let j = 0
		for (let q = 0; q < height; q++) {
			while (z[j + 1] < q) j++
			const p = v[j]
			out[q * width + x] = p * width + rowCol[p * width + x]
		}
	}
	return out
}
